/**
 * Set form layout for GenericEntityUpsertDialog.
 * Maps generic formData (API keys: DisplayName, ManualSetMember, etc.) to SetForm (displayName, manualMembers, etc.)
 * and syncs changes back so the dialog and PATCH use a single source of truth.
 *
 * When used: pass disabled={true} when the form should be read-only (e.g. loading or tier3 read-only).
 * Delay-loaded fields (NON_INDEXED_PROPERTY_STUB) are not supported here; use the generic schema-driven
 * form (no custom layout) for Set if you need per-attribute delay-load.
 */

import React, { useMemo, useCallback } from 'react';
import SetForm, { SetFormData } from '../../common/SetForm';

export interface SetFormLayoutProps {
    formData: Record<string, any>;
    setFormData: (data: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
    /** When true, all fields are read-only (e.g. loading or tier3 entitlement). */
    disabled?: boolean;
    /** Standard mode hides admin-level tools (System toggle, Generate Random, View Attributes). */
    isStandard?: boolean;
}

function formDataToSetFormData(formData: Record<string, any>): SetFormData {
    const typeVal = formData.Type ?? 'Manual';
    const typeLower = typeof typeVal === 'string' ? typeVal.toLowerCase() : 'manual';
    return {
        displayName: formData.DisplayName ?? '',
        description: formData.Description ?? '',
        filterScope: formData.FilterScope ?? '',
        type: (typeLower === 'enumerated' ? 'enumerated' : 'manual') as 'manual' | 'enumerated',
        isSystem: formData.IsSystem ?? false,
        manualMembers: Array.isArray(formData.ManualSetMember) ? formData.ManualSetMember : [],
        filter: formData.Filter ?? ''
    };
}

function setFormDataToApiShape(data: SetFormData): Record<string, any> {
    const typeStr = data.type === 'enumerated' ? 'Enumerated' : 'Manual';
    return {
        DisplayName: data.displayName,
        Description: data.description,
        FilterScope: data.filterScope,
        Type: typeStr,
        IsSystem: data.isSystem,
        ManualSetMember: data.manualMembers,
        Filter: data.filter
    };
}

export const SetFormLayout: React.FC<SetFormLayoutProps> = ({
    formData,
    setFormData,
    disabled = false,
    isStandard = false
}) => {
    const setFormDataMapped = useMemo(() => formDataToSetFormData(formData), [formData]);

    const handleChange = useCallback((newData: SetFormData) => {
        const apiShape = setFormDataToApiShape(newData);
        setFormData(prev => ({ ...prev, ...apiShape }));
    }, [setFormData]);

    return (
        <SetForm
            data={setFormDataMapped}
            onChange={handleChange}
            disabled={disabled}
            showSystemToggle={!isStandard}
            showDescription={true}
            showPreviewMembers={true}
            isStandard={isStandard}
        />
    );
};
